import api from './api';

// Helper function to get the correct API URL
const getApiUrl = () => {
  return (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') 
    ? 'http://localhost:5002' 
    : 'https://investapi.yuukart.com';
};

export interface PublicLink {
  id: number;
  token: string;
  name: string;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
  last_accessed_at: string | null;
  access_count: number;
  url: string;
  is_expired?: boolean;
}

export interface CreateLinkRequest {
  name: string;
  expires_in_days?: number;
}

export const publicLinkService = {
  // Criar novo link público
  async createLink(data: CreateLinkRequest): Promise<PublicLink> {
    const response = await api.post('/public-links', data);
    return response.data;
  },

  // Obter todos os links do usuário
  async getLinks(): Promise<PublicLink[]> {
    const response = await api.get('/public-links');
    return response.data;
  },

  // Remover link
  async deleteLink(linkId: number): Promise<void> {
    await api.delete(`/public-links?id=${linkId}`);
  },

  // Ativar/desativar link
  async toggleLink(linkId: number): Promise<{ is_active: boolean }> {
    const response = await api.post('/public-links/toggle', { id: linkId });
    return response.data;
  },

  // Validar token público (sem autenticação)
  async validatePublicToken(token: string): Promise<{
    valid: boolean;
    link_name: string;
    user_email: string;
  }> {
    const response = await fetch(`${getApiUrl()}/public/validate?token=${token}`);
    if (!response.ok) {
      throw new Error('Token inválido');
    }
    return response.json();
  },

  // Obter dados do dashboard público (sem autenticação)
  async getPublicDashboard(token: string): Promise<any> {
    const response = await fetch(`${getApiUrl()}/public/dashboard?token=${token}`);
    if (!response.ok) {
      throw new Error('Erro ao carregar dashboard');
    }
    return response.json();
  },

  // Métodos para dados lazy-loaded públicos
  async getPublicProductDistribution(token: string): Promise<any> {
    const response = await fetch(`${getApiUrl()}/public/dashboard/product-distribution?token=${token}`);
    if (!response.ok) {
      throw new Error('Erro ao carregar distribuição por produto');
    }
    return response.json();
  },

  async getPublicPlatformDistribution(token: string): Promise<any> {
    const response = await fetch(`${getApiUrl()}/public/dashboard/platform-distribution?token=${token}`);
    if (!response.ok) {
      throw new Error('Erro ao carregar distribuição por plataforma');
    }
    return response.json();
  },

  async getPublicPerformanceIndicators(token: string): Promise<any> {
    const response = await fetch(`${getApiUrl()}/public/dashboard/performance-indicators?token=${token}`);
    if (!response.ok) {
      throw new Error('Erro ao carregar indicadores de performance');
    }
    return response.json();
  },

  async getPublicRebalancingData(token: string): Promise<any> {
    const response = await fetch(`${getApiUrl()}/public/dashboard/rebalancing-data?token=${token}`);
    if (!response.ok) {
      throw new Error('Erro ao carregar dados de rebalanceamento');
    }
    return response.json();
  },

  async getPublicRiskAnalysis(token: string): Promise<any> {
    const response = await fetch(`${getApiUrl()}/public/dashboard/risk-analysis?token=${token}`);
    if (!response.ok) {
      throw new Error('Erro ao carregar análise de risco');
    }
    return response.json();
  },

  async getPublicRecentInvestments(token: string): Promise<any> {
    const response = await fetch(`${getApiUrl()}/public/dashboard/recent-investments?token=${token}`);
    if (!response.ok) {
      throw new Error('Erro ao carregar investimentos recentes');
    }
    return response.json();
  },

  async getPublicInvestedByProduct(token: string): Promise<any> {
    const response = await fetch(`${getApiUrl()}/public/dashboard/invested-by-product?token=${token}`);
    if (!response.ok) {
      throw new Error('Erro ao carregar dados por produto');
    }
    return response.json();
  },

  async getPublicProductPerformance(token: string): Promise<any> {
    const response = await fetch(`${getApiUrl()}/public/dashboard/product-performance?token=${token}`);
    if (!response.ok) {
      throw new Error('Erro ao carregar performance por produto');
    }
    return response.json();
  },

  async getPublicProfitLoss(token: string): Promise<any> {
    const response = await fetch(`${getApiUrl()}/public/dashboard/profit-loss?token=${token}`);
    if (!response.ok) {
      throw new Error('Erro ao carregar dados de P&L');
    }
    return response.json();
  },

  async getPublicProductEvolution(token: string): Promise<any> {
    const response = await fetch(`${getApiUrl()}/public/dashboard/product-evolution?token=${token}`);
    if (!response.ok) {
      throw new Error('Erro ao carregar evolução por produto');
    }
    return response.json();
  },

  async getPublicPortfolioVsMarket(token: string): Promise<any> {
    const response = await fetch(`${getApiUrl()}/public/dashboard/portfolio-vs-market?token=${token}`);
    if (!response.ok) {
      throw new Error('Erro ao carregar comparação portefólio vs mercado');
    }
    return response.json();
  }
};
